<template lang="pug">
  .width-100
    h1.title-big.blue.bolder.m-bottom-5 {{ activeProgramLanguage.name }}
    program-features.features.m-bottom-5(:program='programFeatures')
    .column.m-bottom-2(v-if='showGradeAndYear')
      .row.middle.m-bottom-05
        simple-svg.dark-gray-icon.m-right-1(:filepath='ageIcon' width='18px' height='18px')
        label.text-small.dark-gray {{ $t("detailProgram.levelLabel") }}
      p.text-normal.m-left-3 {{ gradeLabel }}
    .column.m-bottom-2(v-if='showUdls')
      .row.middle.m-bottom-05
        simple-svg.dark-gray-icon.m-right-1(:filepath='udlIcon' width='18px' height='18px')
        label.text-small.dark-gray {{ $t("detailProgram.udls") }}
      .labels.row.middle.m-left-3
        .column(v-if='hasUdl(udlTypes.engagement)')
          label.select.text-small.dark-gray.m-bottom-05
            | {{ $t("udls.type.engagement") }}
          span.text-normal {{ getUdl(udlTypes.engagement) }}
        .column(v-if='hasUdl(udlTypes.representation)')
          label.select.text-small.dark-gray.m-bottom-05
            | {{ $t("udls.type.representation") }}
          span.text-normal {{ getUdl(udlTypes.representation) }}
        .column(v-if='hasUdl(udlTypes.actionAndExpression)')
          label.select.text-small.dark-gray.m-bottom-05
            | {{ $t("udls.type.actionAndExpression") }}
          span.text-normal {{ getUdl(udlTypes.actionAndExpression) }}
    .custom-fields.column(v-if='customKreaFields')
      krea-custom-field-published(
        v-for='field of customKreaFields'
        :key='field.customKreaFieldId'
        :field='field'
        type='educationalProgram'
      )
    .row.width-100.m-bottom-6.m-top-4(v-if='programHasOds')
      .column.width-100
        .row.middle.ods-title-container.m-bottom-2
          img(src='@/assets/SDG Wheel_Transparent_WEB.png' alt='sdg\'s wheel logo' width='24px')
          h3.title-small.black.m-left-1 {{ innerI18n.t('detailProgram.ods') }}
        .sdg-grid.width-100.m-left-3
          span(v-for='anOds in program.odSs.map(ods => ods).sort((a,b)=> a.id - b.id)')
            img.sdg-card(:src='getOdsLogo(anOds.translationId)' :alt='anOds.translationId')
    author-info(:author='programAuthor')
</template>

<script>
import { mapState } from 'vuex'
import { languagesCodesToString } from '../../constants/languages'
import { innerI18n } from '../../main'
import { udlTypes } from '@/constants/udls'
import AuthorBioTooltip from '../AuthorBioTooltip'
import ProgramFeatures from './ProgramFeatures'
import defaultProfileImage from '@/assets/empty_profile.svg'
import KreaCustomFieldPublished from '../KreaCustomFieldPublished'
import AuthorInfo from './AuthorInfo'
import udlIcon from '@/assets/ic_udl.svg'
import ageIcon from '@/assets/ic_ageyear.svg'

export default {
  components: { ProgramFeatures, AuthorBioTooltip, KreaCustomFieldPublished, AuthorInfo },
  data() {
    return {
      innerI18n,
      udlTypes,
      udlIcon,
      ageIcon,
      defaultProfileImage
    }
  },
  computed: {
    ...mapState({
      innerLocale: state => state.behavior.innerLocale,
      program: state => state.educationalProgram.educationalProgram,
      customFields: state => state.userInfo.customFields
    }),
    customKreaFields() {
      return this.program.customKreaFieldOptions.reduce((acc, current) => {
        const existing = acc.find(field => field.customKreaFieldId === current.customKreaFieldId)
        if (existing) {
          existing.values.push({
            id: current.id,
            name: current.name,
            referenceId: current.referenceId,
            type: current.type
          })
        } else {
          acc.push({
            customKreaFieldId: current.customKreaFieldId,
            customKreaFieldName: current.customKreaFieldName,
            customKreaFieldReferenceId: current.customKreaFieldReferenceId,
            values: [{
              id: current.id,
              name: current.name,
              referenceId: current.referenceId,
              type: current.type
            }]
          })
        }
        return acc
      }, [])
    },
    level() {
      return innerI18n.t(`educationalLevels.${this.program.academicGrade.educationalLevelId}`)
    },
    grade() {
      return innerI18n.t(`academicGrades.${this.program.academicGrade.educationalType}.${this.program.academicGrade.translationId}`)
    },
    gradeLabel() {
      return this.level + ' ' + this.grade
    },
    udls() {
      return this.program.udLs
        .map(udl => innerI18n.t(`udls.${udl.referenceId}`))
        .join(', ')
    },
    showUdls() {
      return this.customFields.visibleUDL && this.program.udLs.length
    },
    showGradeAndYear() {
      return this.customFields.visibleGradeAndEducationalLevel && this.program.academicGradeId
    },
    programHasOds() {
      return this.program.odSs.length >= 1
    },
    programFeatures() {
      return {
        resources: this.program.resourceTotalQuantity,
        duration: this.program.classTotalQuantity,
        ageRange: this.program.ageRange,
        secondaryAgeRange: this.program.secondaryAgeRange,
        categoryReferenceId: this.program.categoryReferenceId
      }
    },
    programAuthor() {
      return this.program.mainAuthor || this.program.author
    },
    activeProgramLanguage() {
      return this.program.localizedEducationalPrograms.find(pr => languagesCodesToString[pr.locale] === this.innerLocale)
    }
  },
  methods: {
    getOdsLogo(ods) {
      return require(`@/assets/ods/ES_AR/${ods}.png`)
    },
    hasUdl(type) {
      return this.program.udLs.some(udl => udl.type === type)
    },
    getUdl(type) {
      const refId = this.program.udLs.find(udl => udl.type === type).referenceId
      return innerI18n.t(`udls.${refId}`)
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/sizes';
@import 'src/scss/variables/colors';

.custom-fields{
  gap: 10px;
}

.labels{
  gap: 10px;
  .udls-label{
    display: block;
  }
}

.sdg-grid{
  display: grid;
  grid-template-columns: repeat(auto-fill, 50px);
  gap: 5px;
}

.sdg-card {
  box-shadow: 0 4px 8px 0 $shadow-gray;
  box-sizing: border-box;
  transition: box-shadow 0.3s;
  width: 50px;
  &:hover {
    box-shadow: 0 8px 16px 0 $dark-gray-translusent;
    transform: scale(1.3);
  }
}
</style>
